/**
 * Active Trader workspace — Thinkorswim-style multi-pane layout.
 *
 * Wires the complete backend stack:
 *   - Cognito JWT (auth.getIdToken) on every request
 *   - MFA enforcement surfaced from the amr claim
 *   - REST /v1/broker via BrokerClient (idempotent placeOrder, audit)
 *   - WebSocket /v1 via WsClient (FillEmitted, PositionUpdated, OrderStatusChanged, L2Tick)
 *
 * The visual chrome (rail, top bar, account summary, watchlist, charts grid)
 * is presentation only — every value flows from the same API surface used
 * by the original single-pane terminal.
 */
import { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useAuth } from '../auth/AuthContext';
import { BrokerClient } from '../api/brokerClient';
import { WsClient, type WsEvent, type WsStatus } from '../api/wsClient';
import { TopBar } from './TopBar';
import { BROKERS, SOR_BROKER, type BrokerId } from '../lib/brokers';
import { LeftRail, type RailView } from './LeftRail';
import { AccountSummary } from './AccountSummary';
import { Watchlist, type WatchlistRow } from './Watchlist';
import { ChartsGrid, type ChartCellSpec, type GridLayout } from './ChartsGrid';
import { OrderTicket } from './OrderTicket';
import { PositionsTable, type PositionRow } from './PositionsTable';
import { EventsFeed } from './EventsFeed';
import { ToolsView } from './ToolsView';
import { ScansView } from './ScansView';
import { BondTrader } from './BondTrader';
import { ETFTrader } from './ETFTrader';
import { OrderBlotter } from './OrderBlotter';
import { BrokerSettings } from './BrokerSettings';
import { ReconView } from './ReconView';
import { FundingView } from './FundingView';
import { CorporateActionsView } from './CorporateActionsView';
import { BestExecutionView } from './BestExecutionView';
import { DashboardView } from './DashboardView';
import { TradeConfirmModal } from './TradeConfirmModal';
import { FXTrader } from './FXTrader';
import { RatingsView } from './RatingsView';
import { TradeAlerts } from './TradeAlerts';
import type { BalanceRow } from './AccountBar';

const BROKER_ID = (import.meta as any).env?.VITE_BROKER_ID ?? 'mock';
const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? '/api/v1';
const WS_ENDPOINT = (import.meta as any).env?.VITE_WS_ENDPOINT ?? '';

// Default NGX watchlist for the pilot. In production this comes from a per-user
// preferences endpoint persisted to DynamoDB.
const DEFAULT_WATCHLIST = [
  'MTNN', 'DANGCEM', 'BUACEMENT', 'AIRTELAFRI', 'SEPLAT', 'BUAFOODS', 'BETAGLAS',
  'GTCO', 'SFSREIT', 'ZENITHBANK', 'TRANSPOWER', 'GEREGU', 'NESTLE', 'EUNISELL',
  'ARADEL', 'PRESCO', 'FIRSTHOLDCO',
];

interface Props {
  brokerOverride?: BrokerClient;
  wsOverride?: WsClient;
  initialView?: RailView;
  onExit?: () => void;
}

const TerminalComponent = forwardRef<{ handleNav: (k: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders') => void }, Props>(
  function Terminal({ brokerOverride, wsOverride, initialView, onExit }: Props = {}, ref) {
  const auth = useAuth();
  const [activeBroker, setActiveBroker] = useState<BrokerId>('sor');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const broker = useMemo(() => brokerOverride ?? new BrokerClient({
    baseUrl: API_BASE, getIdToken: auth.getIdToken,
  }), [auth.getIdToken, brokerOverride]);

  const [accountId, setAccountId] = useState<string | undefined>();
  const [accountLabel, setAccountLabel] = useState<string>('All Accounts');
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [balance, setBalance] = useState<BalanceRow | undefined>();
  const [events, setEvents] = useState<Array<{ at: string; e: WsEvent }>>([]);
  const [wsStatus, setWsStatus] = useState<WsStatus>('closed');
  const [accountErr, setAccountErr] = useState<string | undefined>();
  const [fillData, setFillData] = useState<any | null>(null);
  // Dashboard is the canonical post-login landing view. We intentionally ignore
  // any previously-cached nigastock.startPage value here so that a returning
  // tester whose localStorage still holds "Accounts Summary" or "Charts" from a
  // prior build still lands on the tile page. The Start Page dropdown is kept
  // for future deep-link routing but no longer redirects the initial view.
  //
  // If WelcomeHub passes an initialView (because the user clicked a specific
  // product card), honour it on first render. Subsequent navigation via the
  // LeftRail still flows through setView.
  const [view, setView] = useState<RailView>(initialView ?? 'Dashboard');
  const [activeNav, setActiveNav] = useState<'Dashboard' | 'Markets' | 'Portfolio' | 'Orders'>('Dashboard');
  const [marketPrices, setMarketPrices] = useState<Record<string, { mark: number; pct: number; volume?: number }>>({});
  const [selectedSymbol, setSelectedSymbol] = useState('ZENITHBANK');
  const [marketTime, setMarketTime] = useState(now());
  const [chartsGrid, setChartsGrid] = useState<GridLayout>('2x2');
  // View navigation history so Back/Home can work across views.
  const [viewHistory, setViewHistory] = useState<RailView[]>([]);
  const [prefillSide, setPrefillSide] = useState<'BUY' | 'SELL' | undefined>();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const wsRef = useRef<WsClient | undefined>();
  const tradeAlertsRef = useRef<{ addAlert: (asset: string, symbol: string, side: string, quantity: number, price: number, notional: number, type: string, message?: string) => void } | null>(null);

  // Wrap setView so every navigation pushes the previous view onto the history
  // stack. onGoHome clears the stack to match expectations; onGoBack pops.
  const navigateTo = useCallback((next: RailView) => {
    setView((prev) => {
      if (prev !== next) setViewHistory((h) => [...h, prev]);
      return next;
    });
  }, []);
  const goBack = useCallback(() => {
    setViewHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1]!;
      setView(prev);
      return h.slice(0, -1);
    });
  }, []);
  const goHome = useCallback(() => {
    setViewHistory([]);
    setView('Dashboard');
  }, []);
  // Top-bar primary nav. Wired on EVERY view so Dashboard/Markets/Portfolio/
  // Orders links always navigate regardless of which workspace is mounted.
  const handleNav = useCallback((k: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders') => {
    setActiveNav(k);
    if (k === 'Dashboard') navigateTo('Dashboard');
    else if (k === 'Orders') navigateTo('Orders');
    else if (k === 'Portfolio') navigateTo('Monitor');
    else if (k === 'Markets') navigateTo('Trade');
  }, [navigateTo]);

  // Expose handleNav to parent via ref
  useImperativeHandle(ref, () => ({ handleNav }), [handleNav]);

  const closeWorkspace = useCallback(() => {
    // Exit button returns to WelcomeHub/Dashboard
    // If onExit callback provided, use it (for app-level routing)
    // Otherwise sign out (backward compatibility)
    if (onExit) {
      onExit();
    } else {
      auth.signOut();
    }
  }, [onExit, auth]);

  const handleQuickTrade = useCallback((side: 'BUY' | 'SELL', sym: string, _px: number) => {
    setSelectedSymbol(sym);
    setPrefillSide(side);
    navigateTo('Trade');
  }, [navigateTo]);

  // Search box handler — type symbol and press Enter to jump to Trade view
  const handleSymbolSearch = useCallback((sym: string) => {
    if (sym && sym.length > 0) {
      setSelectedSymbol(sym);
      navigateTo('Trade');
    }
  }, [navigateTo]);

  // Create a wrapper to add trade alerts from anywhere
  const captureTradeExecution = useCallback((asset: 'EQUITY' | 'FX' | 'BOND' | 'ETF', symbol: string, side: 'BUY' | 'SELL', quantity: number, price: number, notional: number, type: 'success' | 'error' = 'success', message?: string) => {
    if (tradeAlertsRef.current?.addAlert) {
      tradeAlertsRef.current.addAlert(asset, symbol, side, quantity, price, notional, type, message);
    }
  }, []);

  // Clicking a symbol in the watchlist (from any view) should jump to Trade
  // so the full chart + order ticket are visible and actionable immediately.
  // From inside Trade view, we just update selection without renavigating.
  const pickSymbol = useCallback((sym: string) => {
    setSelectedSymbol(sym);
    setView((prev) => {
      if (prev === 'Trade') return prev;
      setViewHistory((h) => [...h, prev]);
      return 'Trade';
    });
  }, []);

  // Bootstrap accounts once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await broker.accounts(BROKER_ID);
        if (cancelled) return;
        const first = r.accounts?.[0];
        if (!first) { setAccountErr('No linked broker accounts. Link one in Settings.'); return; }
        setAccountId(String(first.accountId));
        setAccountLabel(`${first.displayName ?? first.accountId} (${first.currency ?? 'NGN'})`);
      } catch (e: any) {
        setAccountErr(e?.message ?? 'failed to load accounts');
      }
    })();
    return () => { cancelled = true; };
  }, [broker]);

  // Handle resize for mobile responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    try {
      const [p, b] = await Promise.all([
        broker.positions(BROKER_ID, accountId),
        broker.balances(BROKER_ID, accountId),
      ]);
      setPositions((p.positions ?? []) as PositionRow[]);
      setBalance(((b.balances ?? [])[0]) as BalanceRow | undefined);
    } catch { /* background refresh swallows transient errors */ }
  }, [broker, accountId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // WebSocket subscription — feeds events panel, watchlist marks, and triggers
  // a positions/balance refresh on every fill or position update.
  useEffect(() => {
    const ws = wsOverride ?? (WS_ENDPOINT ? new WsClient({ endpoint: WS_ENDPOINT, getIdToken: auth.getIdToken }) : undefined);
    if (!ws) return;
    wsRef.current = ws;
    const off1 = ws.on((e) => {
      setEvents((prev) => [...prev, { at: new Date().toISOString(), e }]);
      if (e.type === 'FillEmitted') {
        setFillData(e.payload ?? null);
        void refresh();
      } else if (e.type === 'PositionUpdated') {
        void refresh();
      }
      if (e.type === 'L2Tick') {
        const p = (e.payload ?? {}) as Record<string, any>;
        if (typeof p.symbol === 'string' && (typeof p.bid === 'string' || typeof p.bid === 'number')) {
          setMarketPrices((prev) => {
            const prior = prev[p.symbol]?.mark;
            const mark = Number(p.bid);
            const pct = prior ? ((mark - prior) / prior) * 100 : 0;
            return { ...prev, [p.symbol]: { mark, pct, volume: prev[p.symbol]?.volume } };
          });
        }
      }
    });
    const off2 = ws.onStatus(setWsStatus);
    void ws.connect();
    return () => { off1(); off2(); ws.close(); };
  }, [auth.getIdToken, refresh, wsOverride]);

  // Tick the market clock once a second.
  useEffect(() => {
    const id = setInterval(() => setMarketTime(now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Compose the watchlist view from defaults + any live marks we've seen.
  const watchlist = useMemo<WatchlistRow[]>(() => DEFAULT_WATCHLIST.map((sym) => {
    const mp = marketPrices[sym];
    return {
      symbol: sym,
      mark: mp?.mark ?? seededPrice(sym),
      pctChange: mp?.pct ?? seededChange(sym),
      volume: mp?.volume,
    };
  }), [marketPrices]);

  // Derive PnL roll-ups for the account summary.
  const positionsValueKobo = positions.reduce((acc, p) => acc + safeBig(p.marketValue), 0n).toString();
  const pnlOpenKobo = positions.reduce((acc, p) => acc + safeBig(p.unrealizedPnl), 0n).toString();

  const tickerStrip = useMemo(() => {
    const ngxAsi = { sym: 'NGX-ASI', px: '110,245.67', pct: 0.85 };
    const second = watchlist.find((w) => w.symbol === 'DANGCEM') ?? watchlist[0];
    return [ngxAsi, { sym: second?.symbol ?? 'DANGCEM', px: (second?.mark ?? 0).toFixed(2), pct: second?.pctChange ?? 0 }];
  }, [watchlist]);

  // Charts grid — provide up to 6 cells so the 3×2 layout is fully populated.
  // Selecting one in the watchlist promotes it to the top-left cell while
  // keeping the other cells populated with NGX names.
  const chartCells = useMemo<ChartCellSpec[]>(() => {
    const fillers = ['DANGCEM', 'MTNN', 'GTCO', 'ZENITHBANK', 'BUACEMENT', 'SEPLAT', 'AIRTELAFRI'].filter((s) => s !== selectedSymbol);
    const symbols = [selectedSymbol, ...fillers].slice(0, 6);
    return symbols.map((s) => {
      const w = watchlist.find((x) => x.symbol === s);
      const mark = w?.mark ?? seededPrice(s);
      // Mock alerts and corporate-action dates derived from the symbol so they
      // are stable across renders. Real values flow from BAL endpoints in pilot.
      const now = Date.now(); const day = 24 * 3600 * 1000;
      return {
        symbol: s, price: mark, pctChange: w?.pctChange ?? seededChange(s),
        alerts: [{ price: mark * 1.05, direction: 'above' as const, label: 'Resistance' }],
        earningsDates: [now - 30 * day, now - 5 * day],
        dividendsDates: [now - 18 * day],
        positionAvgPrice: s === 'ZENITHBANK' ? mark * 0.98 : undefined,
        positionQuantity: s === 'ZENITHBANK' ? 1000 : undefined,
      };
    });
  }, [selectedSymbol, watchlist]);

  // Dashboard — central launchpad to every workspace.
  if (view === 'Dashboard') {
    return (
      <div className="app">
        <TopBar
          marketStatus="OPEN"
          marketTime={marketTime}
          tickers={tickerStrip}
          wsStatus={wsStatus}
          mfaSatisfied={auth.mfaSatisfied}
          username={auth.username}
          onSignOut={auth.signOut}
          activeNav="Dashboard"
          onNav={handleNav}
          canGoBack={viewHistory.length > 0}
          onBack={goBack}
          onHome={goHome}
          onClose={closeWorkspace}
        />
        <div className="workspace workspace-wide">
          <LeftRail active={view} onChange={navigateTo} />
          <div className="main-col" style={{ padding: 24, overflow: 'auto' }}>
            <DashboardView onNavigate={navigateTo} userName={auth.username} onExitToWelcome={onExit} />
          </div>
        </div>
      </div>
    );
  }
  // Best-Execution Reporting — SEC Rule 606/607 metrics per broker.
  if (view === 'BestEx') {
    return (
      <div className="app">
        <TopBar
          marketStatus="OPEN"
          marketTime={marketTime}
          tickers={tickerStrip}
          wsStatus={wsStatus}
          mfaSatisfied={auth.mfaSatisfied}
          username={auth.username}
          onSignOut={auth.signOut}
          activeNav="Portfolio"
          onNav={handleNav}
          canGoBack={true}
          onBack={goBack}
          onHome={goHome}
          onClose={closeWorkspace}
        />
        <div className="workspace workspace-norail">
          <div className="main-col" style={{ padding: 20, overflow: 'auto' }}>
            <BestExecutionView />
          </div>
        </div>
      </div>
    );
  }
  // Credit & Investment Ratings — ratings from Agusto, GCR, DataPro, Fitch.
  if (view === 'Ratings') {
    return (
      <div className="app">
        <TopBar
          marketStatus="OPEN"
          marketTime={marketTime}
          tickers={tickerStrip}
          wsStatus={wsStatus}
          mfaSatisfied={auth.mfaSatisfied}
          username={auth.username}
          onSignOut={auth.signOut}
          activeNav="Dashboard"
          onNav={handleNav}
          canGoBack={true}
          onBack={goBack}
          onHome={goHome}
          onClose={closeWorkspace}
        />
        <div className="workspace workspace-norail">
          <div className="main-col" style={{ padding: 20, overflow: 'auto' }}>
            <RatingsView />
          </div>
        </div>
      </div>
    );
  }
  // Corporate Actions — dividends, splits, rights issues, AGMs.
  if (view === 'CorpActions') {
    return (
      <div className="app">
        <TopBar
          marketStatus="OPEN"
          marketTime={marketTime}
          tickers={tickerStrip}
          wsStatus={wsStatus}
          mfaSatisfied={auth.mfaSatisfied}
          username={auth.username}
          onSignOut={auth.signOut}
          activeNav="Portfolio"
          onNav={handleNav}
          canGoBack={true}
          onBack={goBack}
          onHome={goHome}
          onClose={closeWorkspace}
        />
        <div className="workspace workspace-norail">
          <div className="main-col" style={{ padding: 20, overflow: 'auto' }}>
            <CorporateActionsView />
          </div>
        </div>
      </div>
    );
  }
  // Funding & Withdrawals.
  if (view === 'Funding') {
    return (
      <div className="app">
        <TopBar
          marketStatus="OPEN"
          marketTime={marketTime}
          tickers={tickerStrip}
          wsStatus={wsStatus}
          mfaSatisfied={auth.mfaSatisfied}
          username={auth.username}
          onSignOut={auth.signOut}
          activeNav="Portfolio"
          onNav={handleNav}
          canGoBack={true}
          onBack={goBack}
          onHome={goHome}
          onClose={closeWorkspace}
        />
        <div className="workspace workspace-norail">
          <div className="main-col" style={{ padding: 20, overflow: 'auto' }}>
            <FundingView />
          </div>
        </div>
      </div>
    );
  }
  // Position Reconciliation — diff local cache vs broker book-of-record.
  if (view === 'Recon') {
    return (
      <div className="app">
        <TopBar
          marketStatus="OPEN"
          marketTime={marketTime}
          tickers={tickerStrip}
          wsStatus={wsStatus}
          mfaSatisfied={auth.mfaSatisfied}
          username={auth.username}
          onSignOut={auth.signOut}
          activeNav="Portfolio"
          onNav={handleNav}
          canGoBack={true}
          onBack={goBack}
          onHome={goHome}
          onClose={closeWorkspace}
        />
        <div className="workspace workspace-norail">
          <div className="main-col" style={{ padding: 20, overflow: 'auto' }}>
            <ReconView />
          </div>
        </div>
      </div>
    );
  }
  // Broker Settings — link, unlink, refresh.
  if (view === 'Brokers') {
    return (
      <div className="app">
        <TopBar
          marketStatus="OPEN"
          marketTime={marketTime}
          tickers={tickerStrip}
          wsStatus={wsStatus}
          mfaSatisfied={auth.mfaSatisfied}
          username={auth.username}
          onSignOut={auth.signOut}
          activeNav="Markets"
          onNav={handleNav}
          canGoBack={true}
          onBack={goBack}
          onHome={goHome}
          onClose={closeWorkspace}
        />
        <div className="workspace workspace-norail">
          <div className="main-col" style={{ padding: 20, overflow: 'auto' }}>
            <BrokerSettings />
          </div>
        </div>
      </div>
    );
  }
  // Cross-broker order blotter.
  if (view === 'Orders') {
    return (
      <div className="app">
        <TopBar
          marketStatus="OPEN"
          marketTime={marketTime}
          tickers={tickerStrip}
          wsStatus={wsStatus}
          mfaSatisfied={auth.mfaSatisfied}
          username={auth.username}
          onSignOut={auth.signOut}
          activeNav="Orders"
          onNav={handleNav}
          canGoBack={true}
          onBack={goBack}
          onHome={goHome}
          onClose={closeWorkspace}
        />
        <div className="workspace workspace-norail">
          <div className="main-col" style={{ padding: 16, overflow: 'auto' }}>
            <OrderBlotter
              onCancel={async () => { /* wires to broker.cancelOrder */ }}
              onModify={async () => { /* wires to broker.modifyOrder */ }}
            />
          </div>
        </div>
      </div>
    );
  }
  // NigaBond Trader gets its own full-page shell (own TopBar + LeftRail).
  if (view === 'Bond') {
    return (
      <BondTrader
        brokerOverride={brokerOverride}
        wsOverride={wsOverride}
        onExitToEquities={() => { setViewHistory([]); setView('Dashboard'); }}
        onExitToWelcome={() => onExit?.()}
        onSwitchView={(v) => { setViewHistory((h) => [...h, 'Bond']); setView(v); }}
      />
    );
  }
  // NigaETF Trader — same full-page shell pattern as bonds.
  if (view === 'ETF') {
    return (
      <ETFTrader
        brokerOverride={brokerOverride}
        wsOverride={wsOverride}
        onExitToEquities={() => { setViewHistory([]); setView('Dashboard'); }}
        onExitToWelcome={() => onExit?.()}
        onSwitchView={(v) => { setViewHistory((h) => [...h, 'ETF']); setView(v); }}
      />
    );
  }
  // FX Trader — dedicated FX trading workspace.
  if (view === 'FX') {
    return (
      <FXTrader
        brokerOverride={brokerOverride}
        wsOverride={wsOverride}
        onExitToEquities={() => { setViewHistory([]); setView('Dashboard'); }}
        onExitToWelcome={() => onExit?.()}
        onSwitchView={(v) => { setViewHistory((h) => [...h, 'FX']); setView(v); }}
      />
    );
  }

  // Render main workspace layout for remaining views: Charts, Analyze, Trade, Monitor, Scan, Tools
  // Use switch statement for TypeScript exhaustiveness checking and maintainability
  const renderMainContent = () => {
    switch (view) {
      case 'Charts':
      case 'Analyze':
        return (
          <div className="charts-view">
            <div className="charts-view-head">
              <h2>Charts</h2>
              <div className="charts-view-actions">
                <button className="ch-pill">Alerts (2)</button>
                <button className="ch-pill">Patterns</button>
                {(['1x1','2x2','3x2'] as const).map((g) => (
                  <button
                    key={g}
                    className={`ch-pill grid-pill ${chartsGrid === g ? 'active accent' : ''}`}
                    onClick={() => setChartsGrid(g)}
                  >Grid {g}</button>
                ))}
              </div>
            </div>
            <div className="charts-view-body">
              <ChartsGrid
                cells={chartCells}
                layout={chartsGrid}
                onSelect={setSelectedSymbol}
                workingOrders={demoWorkingOrders}
                brokerClient={broker}
                brokerId={BROKER_ID}
                onQuickTrade={handleQuickTrade}
              />
            </div>
          </div>
        );

      case 'Trade':
        return (
          <div className="workarea">
            <div className="workarea-head"><span className="tab active">Active Trader</span></div>
            <div className="workarea-body" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 280px', gap: 12, alignItems: 'start' }}>
              <div>
                <div className="panel" style={{ padding: 0, overflow: 'hidden', height: 540 }}>
                  <ChartsGrid
                    cells={[chartCells[0]!]}
                    single
                    onSelect={setSelectedSymbol}
                    workingOrders={demoWorkingOrders}
                    brokerClient={broker}
                    brokerId={BROKER_ID}
                    onQuickTrade={handleQuickTrade}
                  />
                </div>
                <PositionsTable rows={positions} />
                <EventsFeed events={events} />
                {accountErr && <div className="panel" style={{ borderColor: 'var(--red)' }}>{accountErr}</div>}
              </div>
              <div>
                {accountId && ((() => {
                    const sel = watchlist.find((w) => w.symbol === selectedSymbol);
                    const mark = sel?.mark;
                    const pct = sel?.pctChange ?? 0;
                    const seed = (salt: number) => {
                      let h = 2166136261 >>> 0;
                      for (let i = 0; i < selectedSymbol.length; i++) h = Math.imul(h ^ selectedSymbol.charCodeAt(i), 16777619) >>> 0;
                      h = Math.imul(h ^ salt, 16777619) >>> 0; return h;
                    };
                    const sn = (salt: number, lo: number, hi: number) => lo + (seed(salt) % 100000) / 100000 * (hi - lo);
                    const si = (salt: number, lo: number, hi: number) => Math.floor(sn(salt, lo, hi));
                    const bid = mark ? mark * (1 - 0.0008) : undefined;
                    const ask = mark ? mark * (1 + 0.0008) : undefined;
                    const vol = si(10, 80_000, 2_000_000);
                    const dayHigh = mark ? mark * (1 + Math.abs(pct) / 100 + 0.005) : undefined;
                    const dayLow  = mark ? mark * (1 - Math.abs(pct) / 100 - 0.005) : undefined;
                    const week52Hi = mark ? sn(3, mark * 1.05, mark * 1.6) : undefined;
                    const week52Lo = mark ? sn(2, mark * 0.6, mark * 0.9) : undefined;
                    const sectorOf = (sym: string) => {
                      const x = sym.toUpperCase();
                      if (x.endsWith('BANK') || x.startsWith('GT') || x.includes('FIRST') || x.includes('ZENITH')) return 'Financials';
                      if (x.includes('CEM') || x.includes('BUA')) return 'Materials';
                      if (x.includes('MTN') || x.includes('AIRTEL')) return 'Telecom';
                      if (x.includes('SEPLAT') || x.includes('ARADEL') || x.includes('GEREGU') || x.includes('TRANS')) return 'Energy';
                      if (x.includes('NESTLE') || x.includes('FOOD') || x.includes('PRESCO')) return 'Consumer Staples';
                      return 'Industrials';
                    };
                    return (
                      <OrderTicket
                        brokerId={BROKER_ID}
                        accountId={accountId}
                        brokerClient={broker}
                        mfaSatisfied={auth.mfaSatisfied}
                        symbol={selectedSymbol}
                        symbolLongName={prettySymbolName(selectedSymbol)}
                        lastPrice={mark}
                        pctChange={pct}
                        buyingPowerKobo={balance?.buyingPower}
                        onPlaced={() => void refresh()}
                        defaultSide={prefillSide}
                        onSideChanged={() => setPrefillSide(undefined)}
                        bid={bid}
                        ask={ask}
                        bidSize={si(11, 100, 5000)}
                        askSize={si(12, 100, 5000)}
                        volume={sel?.volume ?? vol}
                        dayHigh={dayHigh}
                        dayLow={dayLow}
                        weekHigh52={week52Hi}
                        weekLow52={week52Lo}
                        sector={sectorOf(selectedSymbol)}
                        marketCap={sn(15, 2, 800)}
                        eps={sn(14, -2, 18)}
                        pe={sn(16, 4, 42)}
                      />
                    );
                  })())}
              </div>
            </div>
          </div>
        );

      case 'Monitor':
        return (
          <div className="workarea">
            <div className="workarea-head"><span className="tab active">Monitor</span><span className="tab">Account Statement</span><span className="tab">Activity</span></div>
            <div className="workarea-body">
              <PositionsTable rows={positions} />
              <EventsFeed events={events} />
              {accountErr && <div className="panel" style={{ borderColor: 'var(--red)' }}>{accountErr}</div>}
            </div>
          </div>
        );

      case 'Scan':
        return (
          <ScansView
            universe={watchlist.map((w) => ({ symbol: w.symbol, mark: w.mark, pctChange: w.pctChange }))}
            watchlistSymbols={watchlist.map((w) => w.symbol)}
            onSelectSymbol={setSelectedSymbol}
            onJumpToTrade={() => setView('Trade')}
          />
        );

      case 'Tools':
        return (
          <ToolsView
            symbol={selectedSymbol}
            symbolLongName={prettySymbolName(selectedSymbol)}
            mark={watchlist.find((w) => w.symbol === selectedSymbol)?.mark ?? 0}
            pctChange={watchlist.find((w) => w.symbol === selectedSymbol)?.pctChange ?? 0}
            onJumpToTrade={() => setView('Trade')}
            onAddToWatchlist={(s) => console.log('add to watchlist:', s)}
             onCreateAlert={(a) => console.log('alert:', a)}
          />
        );

      default:
        // TypeScript exhaustiveness check: if this case is hit, a new RailView value wasn't handled above
        const _exhaustive: never = view;
        return _exhaustive;
    }
  };

  return (
    <div className="app">
      <TopBar
        marketStatus="OPEN"
        marketTime={marketTime}
        tickers={tickerStrip}
        wsStatus={wsStatus}
        mfaSatisfied={auth.mfaSatisfied}
        username={auth.username}
        onSignOut={auth.signOut}
        activeBroker={activeBroker}
        onChangeBroker={(id) => setActiveBroker(id as BrokerId)}
        brokerOptions={[
          { id: SOR_BROKER.id, label: SOR_BROKER.label, glyph: SOR_BROKER.logoGlyph },
          ...BROKERS.map((b) => ({ id: b.id, label: b.label, glyph: b.logoGlyph })),
        ]}
        activeNav={activeNav}
        onNav={handleNav}
        canGoBack={viewHistory.length > 0}
        onBack={goBack}
        onHome={goHome}
        onClose={closeWorkspace}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
      />
      <div className={`workspace ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <LeftRail active={view} onChange={navigateTo} />
        <div className="left-col">
          <AccountSummary
            balance={balance}
            accountLabel={accountLabel}
            positionsValueKobo={positionsValueKobo}
            pnlOpenKobo={pnlOpenKobo}
            pnlDayKobo={pnlOpenKobo}
          />
          <Watchlist
            rows={watchlist}
            selected={selectedSymbol}
            onSelect={pickSymbol}
          />
        </div>
        <div className="main-col">
          {renderMainContent()}
        </div>
      </div>
      <TradeConfirmModal
        isOpen={!!fillData}
        fillData={fillData}
        onClose={() => {
          setFillData(null);
          void refresh();
        }}
      />
      <TradeAlerts
        ref={tradeAlertsRef}
        userEmail={auth.username}
        onClear={() => {}}
      />
    </div>
  );
  }
);

TerminalComponent.displayName = 'Terminal';
export const Terminal = TerminalComponent;

function now(): string {
  const d = new Date();
  return `${String(d.getUTCHours() + 1).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} WAT`;
}
function safeBig(s: string | undefined): bigint {
  if (!s) return 0n;
  try { return BigInt(s); } catch { return 0n; }
}
function seededPrice(sym: string): number {
  let h = 5381;
  for (let i = 0; i < sym.length; i++) h = ((h << 5) + h) + sym.charCodeAt(i);
  return 50 + Math.abs(h % 100000) / 100;
}
function seededChange(sym: string): number {
  let h = 0;
  for (let i = 0; i < sym.length; i++) h = (h * 31 + sym.charCodeAt(i)) | 0;
  return ((h % 1000) / 100) - 5;
}

const demoWorkingOrders: any[] = [
  { brokerOrderId: 'demo-wo-1', clOrdId: 'clord-demo-1', accountId: 'acc-demo-1',
    symbol: 'ZENITHBANK', side: 'SELL', type: 'LIMIT', quantity: 500,
    limitPrice: '4900', status: 'WORKING', placedAt: new Date().toISOString() },
];

const NGX_LONG_NAMES: Record<string, string> = {
  ZENITHBANK: 'Zenith Bank', DANGCEM: 'Dangote Cement', MTNN: 'MTN Nigeria',
  GTCO: 'Guaranty Trust Holding', BUACEMENT: 'BUA Cement', AIRTELAFRI: 'Airtel Africa',
  SEPLAT: 'Seplat Energy', BUAFOODS: 'BUA Foods', BETAGLAS: 'Beta Glass',
    NESTLE: 'Nestle Nigeria', GEREGU: 'Geregu Power', TRANSCORP: 'Transnational',
  ARADEL: 'Aradel Holdings', PRESCO: 'Presco', FIRSTBANK: 'FirstBank Holdings',
};

function prettySymbolName(sym: string): string {
  const hit = NGX_LONG_NAMES[sym];
  if (hit) return hit;
  const s = sym.toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

